import axios from 'axios';
import { authFor } from './sessionLink';
import { repeatText } from './quickAdd';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Talking to the meetings endpoints, and saying when a meeting is.
 *
 * Gordon, 22 October: Teams-like meetings, joined by a link from an .ics
 * invitation; outside guests join with a NAME ONLY through a waiting room
 * that the organiser, or any member of staff in the meeting, admits from;
 * recurring meetings; minutes in English that the organiser reviews before
 * anybody else sees them; and the to-dos from a meeting into ONE SHARED
 * LIST or A PRIVATE LIST EACH, the organiser's choice.
 *
 * Everything here is an ordinary axios request carrying the same credential
 * as the rest of the application (authFor: a contractor's token, a guest's
 * `guest:<token>` bearer, else the Entra interceptor). The contract these
 * follow is routes/meetings.js (migration 066); until that migration has
 * run the server answers 503 naming it, and `notReady` recognises that
 * answer so the pages can say so calmly rather than failing.
 *
 * TIMES ARE SAID IN MALTA. A meeting is at an instant; the firm is in one
 * place; so "10:00" means Malta's ten o'clock whoever is reading it and
 * wherever their laptop thinks it is - a colleague on a trip sees the time
 * the room will actually be used at. The form, going the other way, takes
 * the date and time typed as Malta time too, for the same reason.
 */

export const ZONE = 'Europe/Malta';

/* ------------------------------------------------------------ the meetings */

/** Mine, upcoming or past (or everything), newest-first for past, soonest-first for upcoming. */
export const listMeetings = (range = 'upcoming') => axios
  .get(`${API_BASE}/meetings`, { ...authFor(), params: { range } })
  .then((r) => (Array.isArray(r.data) ? r.data : Array.isArray(r.data?.meetings) ? r.data.meetings : []));

export const getMeeting = (id) => axios
  .get(`${API_BASE}/meetings/${id}`, authFor())
  .then((r) => r.data);

export const createMeeting = (body) => axios
  .post(`${API_BASE}/meetings`, body, authFor())
  .then((r) => r.data);

/**
 * START MEETING NOW (090): a meeting for this moment, live, mine, nobody
 * else on it yet - `kind` 'room' (the phone on the table) or 'call' (a call
 * on another app, with `platform`). The page opens the recorder on it at
 * once. 201 with the meeting; 503 naming migrate:090 before the migration.
 */
export const startMeetingNow = (body = {}) => axios
  .post(`${API_BASE}/meetings/now`, body, authFor())
  .then((r) => r.data);

/** The apps a call can be on, as the server names them and as the screen says them. */
export const PLATFORMS = [
  ['teams', 'Microsoft Teams'], ['zoom', 'Zoom'], ['slack', 'Slack huddle'], ['phone', 'Phone on speaker'], ['other', 'Another app']
];
export const platformWords = (p) => (PLATFORMS.find(([k]) => k === p) || [])[1] || null;

/** What kind of meeting, in a word or two, for a chip. */
export const kindWords = (m) => (m?.kind === 'room' ? (m.location || 'In the room')
  : m?.kind === 'call' ? `Call${m.platform ? ` on ${platformWords(m.platform) || m.platform}` : ' on another app'}` : 'Online');

/** `scope` is for a series: 'this' occurrence, this and 'following', or 'all'. */
export const updateMeeting = (id, body, scope = 'this') => axios
  .patch(`${API_BASE}/meetings/${id}`, { ...body, scope }, authFor())
  .then((r) => r.data);

export const cancelMeeting = (id, scope = 'this') => axios
  .delete(`${API_BASE}/meetings/${id}`, { ...authFor(), data: { scope } })
  .then((r) => r.data);

/** My answer to the invitation. */
export const respondToMeeting = (id, response) => axios
  .post(`${API_BASE}/meetings/${id}/respond`, { response }, authFor())
  .then((r) => r.data);

/** The call behind an online meeting, created on first arrival; then the usual /sessions/:id/join. */
export const openMeetingSession = (id) => axios
  .post(`${API_BASE}/meetings/${id}/session`, {}, authFor())
  .then((r) => r.data);

/* ---------------------------------------------------------------- guests */

/**
 * What the invitation link says before anybody signs anything: the meeting,
 * the name it was sent to, and whether there is a waiting room. No
 * credential - the token in the address is the whole claim.
 */
export const guestInfo = (token) => axios
  .get(`${API_BASE}/join/${encodeURIComponent(token)}`)
  .then((r) => r.data);

/**
 * "Ask to join", with a name. Answers { session_id, participant_id,
 * bearer: 'guest:<token>' }; the bearer goes on every session request after
 * this (sessionLink setGuestAuth).
 */
export const guestJoin = (token, name) => axios
  .post(`${API_BASE}/join/${encodeURIComponent(token)}/session`, { name })
  .then((r) => r.data);

/** Let a guest in, or not. Staff only; the server checks. */
export const admitGuest = (sessionId, participantId, allow) => axios
  .post(`${API_BASE}/sessions/${sessionId}/admit`, { participant_id: participantId, allow: Boolean(allow) }, authFor())
  .then((r) => r.data);

/* --------------------------------------------------------------- minutes */

/** Write minutes from a recording, with an optional instruction. 201 { id, version, body_md, json, state: 'draft' }. */
export const createMinutes = (recordingId, prompt) => axios
  .post(`${API_BASE}/recordings/${recordingId}/minutes`, prompt ? { prompt } : {}, authFor())
  .then((r) => r.data);

export const editMinutes = (id, bodyMd) => axios
  .patch(`${API_BASE}/minutes/${id}`, { body_md: bodyMd }, authFor())
  .then((r) => r.data);

export const approveMinutes = (id) => axios
  .post(`${API_BASE}/minutes/${id}/approve`, {}, authFor())
  .then((r) => r.data);

/** To 'attendees' (guests included when the meeting says so) or to 'staff' only. Answers { sent_to }. */
export const sendMinutes = (id, to = 'attendees') => axios
  .post(`${API_BASE}/minutes/${id}/send`, { to }, authFor())
  .then((r) => r.data);

export const listMinutes = (meetingId) => axios
  .get(`${API_BASE}/meetings/${meetingId}/minutes`, authFor())
  .then((r) => (Array.isArray(r.data) ? r.data : Array.isArray(r.data?.minutes) ? r.data.minutes : []));

/* -------------------------------------------------------- reading errors */

/** The server has the code and not the tables yet: 503 naming migrate:066. */
export const notReady = (err) => {
  if (err?.response?.status !== 503) return false;
  const body = err.response.data;
  const text = typeof body === 'string' ? body : JSON.stringify(body || '');
  return /migrate:066|meetings/i.test(text);
};

/** Ad-hoc meetings (090) specifically: the code is there, migration 090 is not. */
export const adhocNotReady = (err) => err?.response?.status === 503 && /migrate:090/.test(JSON.stringify(err.response.data || ''));

/** The sentence to show for a failed call, from the server when it gave one. */
export const reasonOf = (err, fallback) => {
  const d = err?.response?.data;
  if (d?.refused && d.reason) return `The model declined: ${d.reason}`;
  return d?.error || d?.detail || d?.reason || fallback;
};

/* ----------------------------------------------------------- saying when */

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n) => String(n).padStart(2, '0');

/**
 * An instant, broken into Malta's calendar: { y, m (1-12), d, wd (0 Sun),
 * hh, mm, key: 'YYYY-MM-DD' }. Through Intl rather than arithmetic, because
 * Malta changes its clocks twice a year and the browser may not be in Malta.
 */
export const inMalta = (iso) => {
  const at = iso instanceof Date ? iso : new Date(iso);
  if (!iso || Number.isNaN(at.getTime())) return null;
  const parts = {};
  try {
    for (const p of new Intl.DateTimeFormat('en-GB', {
      timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit', weekday: 'short',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(at)) parts[p.type] = p.value;
  } catch {
    return { y: at.getFullYear(), m: at.getMonth() + 1, d: at.getDate(), wd: at.getDay(),
      hh: at.getHours(), mm: at.getMinutes(), key: `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}` };
  }
  const y = Number(parts.year); const m = Number(parts.month); const d = Number(parts.day);
  return {
    y, m, d, wd: Math.max(0, WD.indexOf(parts.weekday)),
    hh: Number(parts.hour) % 24, mm: Number(parts.minute),
    key: `${y}-${pad(m)}-${pad(d)}`
  };
};

const dayNumber = (p) => Date.UTC(p.y, p.m - 1, p.d) / 86400000;

/** "Today", "Tomorrow", "Yesterday", else "Thu 23 Oct" (+ the year when it is not this one). */
export const dayOf = (iso, now = new Date()) => {
  const p = inMalta(iso);
  const n = inMalta(now);
  if (!p || !n) return '';
  const diff = dayNumber(p) - dayNumber(n);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  if (diff === -1) return 'Yesterday';
  const base = `${WD[p.wd]} ${p.d} ${MON[p.m - 1]}`;
  return p.y === n.y ? base : `${base} ${p.y}`;
};

/** "10:00". */
export const timeOf = (iso) => {
  const p = inMalta(iso);
  return p ? `${pad(p.hh)}:${pad(p.mm)}` : '';
};

/** "45 min", "1 h", "1 h 30 min", "2 days". */
export const lengthOf = (startsAt, endsAt) => {
  const ms = new Date(endsAt) - new Date(startsAt);
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const m = Math.round(ms / 60000);
  if (m < 60) return `${m} min`;
  if (m >= 1440 && m % 1440 === 0) return m === 1440 ? '1 day' : `${m / 1440} days`;
  const h = Math.floor(m / 60);
  return m % 60 === 0 ? `${h} h` : `${h} h ${pad(m % 60)} min`;
};

/** "Today · 10:00–11:00", or across days "Thu 23 Oct 10:00 – Fri 24 Oct 09:00". */
export const whenOf = (startsAt, endsAt, now = new Date()) => {
  const a = inMalta(startsAt);
  const b = inMalta(endsAt);
  if (!a) return '';
  if (!b || a.key === b.key) return `${dayOf(startsAt, now)} · ${timeOf(startsAt)}${b ? `–${timeOf(endsAt)}` : ''}`;
  return `${dayOf(startsAt, now)} ${timeOf(startsAt)} – ${dayOf(endsAt, now)} ${timeOf(endsAt)}`;
};

/** The fifteen minutes before the start when Join is offered (Gordon: "within 15 min before start or while live"). */
export const JOIN_BEFORE_MS = 15 * 60 * 1000;

/**
 * Whether Join should be offered: while it is live, or in the quarter of an
 * hour before it starts, and never once it is over or cancelled. An online
 * meeting only - a meeting in the room has nothing to join.
 */
export const canJoin = (m, now = new Date()) => {
  if (!m || m.kind === 'room') return false;
  if (m.state === 'ended' || m.state === 'cancelled') return false;
  if (m.state === 'live') return true;
  const start = new Date(m.starts_at).getTime();
  const end = new Date(m.ends_at).getTime();
  const t = now.getTime();
  if (!Number.isFinite(start)) return false;
  return t >= start - JOIN_BEFORE_MS && (!Number.isFinite(end) || t <= end + JOIN_BEFORE_MS);
};

/** The chip's word for a meeting's state. */
export const stateWords = (m, now = new Date()) => {
  if (!m) return '';
  if (m.state === 'live') return 'Live';
  if (m.state === 'cancelled') return 'Cancelled';
  if (m.state === 'ended') return 'Ended';
  // 'scheduled' past its end is over whatever the row still says.
  const end = new Date(m.ends_at).getTime();
  if (Number.isFinite(end) && end < now.getTime()) return 'Ended';
  return 'Scheduled';
};

/**
 * A recurrence in words - "Every Monday", "Every 2 weeks on Mon and Thu until
 * 31 Dec", "Every month on the 1st, 6 times". The same words the to-do
 * repeats use, since it is the same shape (lib/quickAdd repeatText), with
 * `count` added because an invitation can say how many.
 */
export const recurrenceText = (rec, now = new Date()) => {
  if (!rec || !rec.freq) return '';
  let text = repeatText({ freq: rec.freq, interval: rec.interval, weekdays: rec.weekdays,
    monthday: rec.monthday, until: rec.until }, { now });
  if (rec.count) text += `, ${rec.count} time${Number(rec.count) === 1 ? '' : 's'}`;
  return text;
};

/* ------------------------------------------------------------- the form */

/**
 * The form's date and time fields are MALTA time (see the top of the file);
 * these turn 'YYYY-MM-DD' + 'HH:MM' into an instant and back. Malta's
 * offset at that moment is read from Intl, so a meeting set for a morning
 * in March still says 09:00 after the clocks go forward.
 */
const offsetMinutes = (at) => {
  try {
    const parts = {};
    for (const p of new Intl.DateTimeFormat('en-GB', { timeZone: ZONE, timeZoneName: 'longOffset' }).formatToParts(at)) {
      parts[p.type] = p.value;
    }
    const m = /GMT([+-])(\d{2}):?(\d{2})?/.exec(parts.timeZoneName || '');
    if (!m) return 0;
    return (m[1] === '-' ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3] || 0));
  } catch {
    return -at.getTimezoneOffset();
  }
};

export const toInstant = (date, time) => {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date || '');
  const tm = /^(\d{1,2}):(\d{2})/.exec(time || '');
  if (!dm || !tm) return null;
  // Guess the offset at the UTC reading, then correct once with the offset
  // at the instant that gives - enough for a zone that moves by one hour.
  const asUtc = Date.UTC(Number(dm[1]), Number(dm[2]) - 1, Number(dm[3]), Number(tm[1]), Number(tm[2]));
  let at = new Date(asUtc - offsetMinutes(new Date(asUtc)) * 60000);
  at = new Date(asUtc - offsetMinutes(at) * 60000);
  return at.toISOString();
};

/** { date: 'YYYY-MM-DD', time: 'HH:MM' } in Malta, for the form's fields. */
export const toFields = (iso) => {
  const p = inMalta(iso);
  return p ? { date: p.key, time: `${pad(p.hh)}:${pad(p.mm)}` } : { date: '', time: '' };
};

/** The next round half hour from now, in Malta, as the form's default. */
export const nextHalfHour = (now = new Date()) => {
  const at = new Date(Math.ceil((now.getTime() + 60000) / 1800000) * 1800000);
  return toFields(at.toISOString());
};

/** The address a guest or colleague follows: the server's, or this site's page for it. */
export const joinUrlOf = (m) => {
  if (m?.join_url) return m.join_url;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return m?.id ? `${origin}/meetings/${m.id}` : '';
};

/** 'u:<id>' / 'c:<id>' from a chat directory row, as the to-do app names people. */
export const keyOf = (p) => (p && (p.key
  || (p.kind && p.id ? `${p.kind === 'contractor' ? 'c' : 'u'}:${p.id}` : null))) || null;
